import { AIButton, useAIAssistant } from '@cobliteam/ai';
import { Analytics } from '@cobliteam/analytics';
import type { SessionUser } from '@cobliteam/common-types';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import GlobalSearch from '@cobliteam/global-search';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import Button from '@cobliteam/ui/components/Button';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import AppUserButton from '@cobliteam/ui-business/components/AppUser';
import Feedback from '@cobliteam/ui-business/components/Feedback';
import { memo } from 'react';
import { SidebarTrigger } from '../AppSidebar';
import FilterGroupComponent from './components/FilterGroupComponent';
import LogoComponent from './components/LogoComponent';
import NotificationsButton from './components/Notifications/NotificationsButton';

interface AppHeaderProps {
  user: SessionUser;
  logout: () => void;
}

const AppHeaderAIButton = ({ isAIRouter }: { isAIRouter: boolean }) => {
  const { isAISidepanelOpen, closeAISidepanel, openAISidepanel } = useAIAssistant();

  return (
    <AIButton
      onClick={() => {
        isAISidepanelOpen ? closeAISidepanel() : openAISidepanel();
      }}
      showTooltip
      isSelected={isAISidepanelOpen}
      dataTestId='app-header-ai-button'
      size='sm'
      disabled={isAIRouter}
      variant={isAISidepanelOpen ? 'solid' : 'soft'}
    />
  );
};

const AppHeader = ({ user, logout }: AppHeaderProps) => {
  const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
  const { runtimeFlags, sidebar } = useDashboard();
  const featureFlags = useFeatureFlags();
  const isAIRouter = sidebar.route?.id === 'ai';

  return (
    <header
      data-testid='app-header'
      className={cn(
        'sticky top-0 z-50 grid h-14 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 border-gray-6 border-b bg-white px-2 pl-4 shadow-sm transition-shadow duration-200'
      )}
    >
      <div className='relative z-10 flex min-w-0 shrink-0 items-center justify-start gap-1.5 md:gap-2'>
        <SidebarTrigger className='-ml-1 mr-2 desktop:hidden rounded-lg border border-gray-5' />
        <LogoComponent />
        <FilterGroupComponent />
      </div>

      <div className='min-w-0 px-1'>
        <div className='flex min-w-0 justify-end min-[961px]:mx-auto min-[961px]:justify-center'>{!runtimeFlags.noValidGroupOrVehicles && <GlobalSearch />}</div>
      </div>

      <div className='relative z-10 ml-auto flex shrink-0 items-center justify-end gap-2.5'>
        {!isDemoMode && featureFlags.aiChat && <AppHeaderAIButton isAIRouter={isAIRouter} />}
        <Feedback source='navbar'>
          <Button
            data-testid='app-header-feedback-button'
            variant='outlined'
            color='default'
            size='sm'
            icon={<Icon name='megaphone' />}
            className='group'
            onClick={() => Analytics.track('NavBar - Feedback - Click')}
          />
        </Feedback>
        <NotificationsButton />
        <AppUserButton user={user} logout={logout} />
      </div>
    </header>
  );
};

export default memo(AppHeader, (prevProps, nextProps) => {
  return prevProps.user.id === nextProps.user.id;
});
